/**
 * Portal job detail (tracker item 45): date, arrival window, status, property
 * address, work items, technician first name. No prices, no notes, nothing
 * internal. The reschedule request (phase 4) is the section at the bottom.
 */
import React, { useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePortalApi } from './PortalContext';
import { usePortalLoad } from './usePortalLoad';
import { FailureBlock, LoadingBlock } from './PortalStates';
import { JobRescheduleSlot } from './PortalExtensionPoints';
import { PORTAL_JOB_STATUS_LABELS, portalAddress, portalDay, portalJobStatusClass, portalWindow } from '../../utils/portal';

export function PortalJobPage() {
  const { id = '' } = useParams<{ id: string }>();
  const api = usePortalApi();
  const load = useCallback(() => api.getPortalJob(id), [api, id]);
  const { data: job, loading, failure, reload } = usePortalLoad(load, 'We could not load this visit.', id);

  if (loading && !job) return <LoadingBlock label="Loading visit" />;
  if (failure) {
    return (
      <div className="space-y-3">
        <FailureBlock failure={failure} onRetry={failure.kind === 'not-found' ? undefined : () => void reload()} />
        <Link to="/portal/jobs" className="inline-flex min-h-[44px] items-center text-sm font-semibold text-blue-700 hover:underline">Back to your visits</Link>
      </div>
    );
  }
  if (!job) return null;

  return (
    <div className="space-y-4">
      <Link to="/portal/jobs" className="inline-flex min-h-[44px] items-center text-sm font-semibold text-blue-700 hover:underline">&larr; Visits</Link>
      <section className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-lg font-bold text-gray-900">{portalDay(job.scheduledDate)}</h1>
          <span className={`rounded px-2 py-0.5 text-xs font-semibold ${portalJobStatusClass(job.status)}`}>{PORTAL_JOB_STATUS_LABELS[job.status]}</span>
        </div>
        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-gray-600">When</dt><dd>{portalWindow(job)}</dd>
          <dt className="text-gray-600">Where</dt><dd data-testid="job-address">{portalAddress(job.property)}</dd>
          <dt className="text-gray-600">Technician</dt><dd data-testid="job-technician">{job.technician}</dd>
          {job.completedDate && <><dt className="text-gray-600">Completed</dt><dd>{portalDay(job.completedDate)}</dd></>}
        </dl>
      </section>
      <section aria-labelledby="work-heading" className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
        <h2 id="work-heading" className="mb-2 text-sm font-semibold text-gray-600">Work</h2>
        {job.items.length === 0 ? (
          <p className="text-sm text-gray-600">The work for this visit has not been listed yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100" aria-label="Work items">
            {job.items.map((item, index) => (
              <li key={index} className="flex justify-between gap-4 py-2 text-sm">
                <span className="min-w-0 break-words">{item.name}</span>
                <span className="whitespace-nowrap text-gray-600">× {item.quantity}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
      <JobRescheduleSlot job={job} onChanged={() => void reload()} />
    </div>
  );
}
